import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Building2, Truck, Leaf, ShieldCheck } from 'lucide-react';
import { Reveal, StaggeredGroup, StaggeredItem } from './Reveal';

const audiences = [
  {
    icon: Globe,
    title: 'Importers & exporters',
    desc: 'Cross-border teams shipping multi-document loads under tightening customs controls.',
    detail: 'Per-lane checklists',
  },
  {
    icon: Building2,
    title: 'SMEs',
    desc: 'Small and mid-sized businesses operating without dedicated compliance staff.',
    detail: 'No customs broker required',
  },
  {
    icon: Truck,
    title: 'Freight forwarders',
    desc: 'Pre-review client paperwork before it reaches customs brokers and carriers.',
    detail: 'Reduce broker rejections',
  },
  {
    icon: Leaf,
    title: 'Food & organic exporters',
    desc: 'EU organic labelling, Certificate of Analysis, origin and quality compliance.',
    detail: 'EU 2018/848 ready',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance teams',
    desc: 'Centralise audit trails, validation history and reviewer sign-off across shipments.',
    detail: 'Audit-ready by default',
  },
];

export default function UseCasesSection() {
  return (
    <section id="use-cases" className="py-24 md:py-32 bg-navy-900/30 border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <Reveal className="flex items-end justify-between mb-14 md:mb-20 flex-wrap gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-glow font-mono mb-4">Built for</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
              Teams shipping across borders, every day
            </h2>
          </div>
          <p className="text-slate-400 max-w-md leading-relaxed">
            Start with pre-shipment review. Expand to validation history, tenant workspaces, and broker handoff as your operation scales.
          </p>
        </Reveal>

        <StaggeredGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" stagger={0.06}>
          {audiences.map((a, i) => (
            <StaggeredItem key={a.title}>
              <motion.div
                whileHover={{ y: -4, borderColor: 'rgba(56, 189, 248, 0.4)' }}
                transition={{ duration: 0.25 }}
                className="rounded-xl border border-white/10 bg-navy-950/40 p-6 h-full flex flex-col transition-colors"
                data-testid={`usecase-${i}`}
              >
                <a.icon className="h-6 w-6 text-cyan-glow mb-5" />
                <div className="font-display text-base font-semibold text-white">{a.title}</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{a.desc}</p>
                <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-cyan-glow font-mono tracking-wide">
                  {a.detail}
                </div>
              </motion.div>
            </StaggeredItem>
          ))}
        </StaggeredGroup>
      </div>
    </section>
  );
}
